'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface ReturnRequest {
  id: string;
  order_id: string;
  order_number: string;
  product_name: string;
  seller_name: string;
  return_reason: string;
  reason_description: string | null;
  return_status: string;
  refund_amount: number | null;
  merchant_response: string | null;
  created_at: string;
  updated_at: string;
}

const RETURN_REASONS = [
  { value: 'damaged_product', label: 'Damaged Product' },
  { value: 'wrong_item', label: 'Wrong Item Received' },
  { value: 'missing_parts', label: 'Missing Parts' },
  { value: 'not_as_described', label: 'Not as Described' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'changed_mind', label: 'Changed Mind' },
  { value: 'other', label: 'Other' },
];

const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: string }> = {
  pending:          { label: 'Pending',          cls: 'bg-amber-100 text-amber-700',   icon: 'ClockIcon' },
  under_review:     { label: 'Under Review',     cls: 'bg-blue-100 text-blue-700',     icon: 'MagnifyingGlassIcon' },
  approved:         { label: 'Approved',         cls: 'bg-green-100 text-green-700',   icon: 'CheckCircleIcon' },
  rejected:         { label: 'Rejected',         cls: 'bg-red-100 text-red-700',       icon: 'XCircleIcon' },
  refund_initiated: { label: 'Refund Initiated', cls: 'bg-purple-100 text-purple-700', icon: 'ArrowUturnLeftIcon' },
  refunded:         { label: 'Refunded',         cls: 'bg-green-100 text-green-700',   icon: 'CheckCircleIcon' },
  closed:           { label: 'Closed',           cls: 'bg-slate-100 text-slate-600',   icon: 'ArchiveBoxIcon' },
};

const EMPTY_FORM = {
  order_number: '',
  product_name: '',
  seller_name: '',
  return_reason: 'damaged_product',
  reason_description: '',
};

export default function CustomerReturnsPage() {
  const router = useRouter();
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [userId, setUserId] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchReturns = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('product_returns')
      .select('id, order_id, order_number, product_name, seller_name, return_reason, reason_description, return_status, refund_amount, merchant_response, created_at, updated_at')
      .eq('user_id', uid)
      .order('created_at', { ascending: false });
    setReturns(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      setUserId(user.id);
      fetchReturns(user.id);
    });
  }, [router, fetchReturns]);

  const handleSubmit = async () => {
    if (!form.order_number || !form.product_name) {
      showToast('Please fill in all required fields.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('product_returns').insert({
        user_id: userId,
        order_number: form.order_number,
        product_name: form.product_name,
        seller_name: form.seller_name || '',
        return_reason: form.return_reason,
        reason_description: form.reason_description || null,
        return_status: 'pending',
        order_source: 'platform_orders',
      });
      if (error) throw error;
      showToast('Return request submitted successfully!', 'success');
      setShowForm(false);
      setForm(EMPTY_FORM);
      fetchReturns(userId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit return request.';
      showToast(message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomerDashboardLayout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-500 shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-700 text-slate-900">Return Requests</h1>
            <p className="text-sm text-slate-500 mt-0.5">Request returns and track their status</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary px-4 py-2.5 rounded-xl text-sm font-600 flex items-center gap-2"
          >
            <Icon name="PlusIcon" size={16} /> New Return
          </button>
        </div>

        {/* New Return Form */}
        {showForm && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="text-base font-700 text-slate-900">Submit Return Request</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Order Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.order_number}
                  onChange={e => setForm(f => ({ ...f, order_number: e.target.value }))}
                  placeholder="e.g. ORD-123456"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Product Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.product_name}
                  onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
                  placeholder="Name of the product"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Seller Name</label>
                <input
                  type="text"
                  value={form.seller_name}
                  onChange={e => setForm(f => ({ ...f, seller_name: e.target.value }))}
                  placeholder="Seller / Store name"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Return Reason <span className="text-red-500">*</span></label>
                <select
                  value={form.return_reason}
                  onChange={e => setForm(f => ({ ...f, return_reason: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {RETURN_REASONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-600 text-slate-500 mb-1.5">Additional Description</label>
              <textarea
                value={form.reason_description}
                onChange={e => setForm(f => ({ ...f, reason_description: e.target.value }))}
                rows={3}
                placeholder="Describe the issue in detail…"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-60 flex items-center gap-2"
              >
                {submitting ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" /> Submitting…</> : 'Submit Request'}
              </button>
              <button
                onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
                className="px-6 py-2.5 rounded-xl text-sm font-600 border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Returns list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : returns.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Icon name="ArrowUturnLeftIcon" size={24} className="text-slate-300" />
              </div>
              <p className="text-sm text-slate-400">No return requests yet</p>
              <p className="text-xs text-slate-400 mt-1">Submit a return request if you have an issue with an order</p>
            </div>
          ) : (
            returns.map(ret => {
              const cfg = STATUS_CONFIG[ret.return_status] || STATUS_CONFIG.pending;
              const isExpanded = expanded === ret.id;
              const reasonLabel = RETURN_REASONS.find(r => r.value === ret.return_reason)?.label || ret.return_reason;
              return (
                <div key={ret.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div
                    className="px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : ret.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
                        <Icon name={cfg.icon as never} size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-700 text-slate-900 truncate">{ret.product_name}</p>
                          <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Order #{ret.order_number} · {reasonLabel}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-400">
                        {new Date(ret.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-slate-400" />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-slate-400">Order Number</p>
                          <p className="font-500 text-slate-800 mt-0.5">#{ret.order_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Seller</p>
                          <p className="font-500 text-slate-800 mt-0.5">{ret.seller_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Status</p>
                          <span className={`inline-flex items-center gap-1 text-xs font-500 px-2 py-0.5 rounded-full mt-0.5 ${cfg.cls}`}>
                            <Icon name={cfg.icon as never} size={12} />
                            {cfg.label}
                          </span>
                        </div>
                        {ret.refund_amount != null && ret.refund_amount > 0 && (
                          <div>
                            <p className="text-xs text-slate-400">Refund Amount</p>
                            <p className="font-600 text-green-700 mt-0.5">₹{(ret.refund_amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-slate-400">Submitted</p>
                          <p className="font-500 text-slate-800 mt-0.5">
                            {new Date(ret.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      {ret.reason_description && (
                        <div>
                          <p className="text-xs text-slate-400">Your Description</p>
                          <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">{ret.reason_description}</p>
                        </div>
                      )}
                      {ret.merchant_response && (
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                          <p className="text-xs font-600 text-blue-700 mb-1">Seller Response</p>
                          <p className="text-xs text-blue-800 leading-relaxed">{ret.merchant_response}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
